import { faEnvelope, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import React from "react";
import { Alert, Badge, Button, Pagination, Stack } from "@mantine/core";
import { CommunicationMechanism, CommunicationType } from "../../../data/api/adminCommunicationApi";
import Loader from "../../common/loader";

// Simple styles without complex theming
const communicationItemStyles = {
  cursor: 'pointer',
};

interface CommunicationsListProps {
  communications: any[];
  persons: { [id: string]: any };
  selectedId: string | null;
  onSelect: (id: string) => void;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  onPageChange: (page: number) => void;
  loading: boolean;
  error: string | null;
}

const CommunicationsList: React.FunctionComponent<CommunicationsListProps> = ({
  communications,
  persons,
  selectedId,
  onSelect,
  pagination,
  onPageChange,
  loading,
  error,
}) => {
  const formatDate = (date: string | Date) => {
    const dt = DateTime.fromISO(date.toString());
    const now = DateTime.now();
    const diffDays = Math.floor(now.diff(dt, 'days').days);
    
    if (diffDays === 0) {
      return dt.toFormat('h:mm a');
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return dt.toFormat('EEE');
    } else {
      return dt.toFormat('MMM d');
    }
  };

  const getPersonName = (personId: string) => {
    const person = persons[personId];
    return person ? person.preferredName || person.fullName : 'Unknown';
  };

  const getRecipients = (to: string | string[]) => {
    if (Array.isArray(to)) {
      if (to.length === 1) {
        return getPersonName(to[0]);
      } else if (to.length <= 3) {
        return to.map(id => getPersonName(id)).join(', ');
      } else {
        return `${getPersonName(to[0])} and ${to.length - 1} others`;
      }
    } else {
      return getPersonName(to);
    }
  };

  const getMechanismBadge = (mechanism: CommunicationMechanism) => {
    switch (mechanism) {
      case CommunicationMechanism.email:
        return <Badge color="blue" className="me-1">Email</Badge>;
      case CommunicationMechanism.sms:
        return <Badge color="green" className="me-1">SMS</Badge>;
      case CommunicationMechanism.notification:
        return <Badge color="yellow" className="me-1">Push</Badge>;
      default:
        return <Badge color="gray" className="me-1">Unknown</Badge>;
    }
  };

  const getCommunicationTypeBadge = (communicationType?: string) => {
    if (!communicationType) return null;
    
    const type = communicationType as CommunicationType;
    switch (type) {
      case CommunicationType.Rostered:
        return <Badge color="blue" className="me-1">Rostered</Badge>;
      case CommunicationType.Unrostered:
        return <Badge color="yellow" className="me-1">Unrostered</Badge>;
      case CommunicationType.BankDetailsChanged:
        return <Badge color="gray" className="me-1">Bank Details</Badge>;
      case CommunicationType.PaymentNotification:
        return <Badge color="green" className="me-1">Payment</Badge>;
      default:
        return null;
    }
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    const currentPage = pagination.page;
    const totalPages = pagination.totalPages;
    
    // Always show first page
    if (currentPage > 3) {
      pages.push(1);
      if (currentPage > 4) pages.push('...');
    }
    
    // Show pages around current page
    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
      pages.push(i);
    }
    
    // Always show last page
    if (currentPage < totalPages - 2) {
      if (currentPage < totalPages - 3) pages.push('...');
      pages.push(totalPages);
    }

    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem', borderTop: '1px solid var(--mantine-color-gray-3)' }}>
        <Pagination 
          total={totalPages}
          value={currentPage}
          onChange={onPageChange}
          size="sm"
        />
      </div>
    );
  };

  if (loading && communications.length === 0) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="p-3">
        <Alert color="red">
          <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
          Error loading communications: {error}
        </Alert>
      </div>
    );
  }

  if (communications.length === 0) {
    return (
      <div className="p-3 text-center">
        <FontAwesomeIcon icon={faEnvelope} size="2x" className="text-muted mb-3" />
        <p className="text-muted">No communications found</p>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100">
      <Stack 
        gap={0}
        className="flex-grow-1 overflow-auto"
        style={{
          backgroundColor: 'var(--color-bg)',
          color: 'var(--color-text-primary)'
        }}
      >
        {communications.map((communication) => (
          <div
            key={communication._id}
            onClick={() => onSelect(communication._id)}
            className="border-0 border-bottom communication-item"
            style={{
              ...communicationItemStyles,
              backgroundColor: selectedId === communication._id 
                ? 'var(--color-bg-disabled-card)' 
                : 'var(--color-bg-card)',
              borderColor: 'var(--color-divider)',
              color: 'var(--color-text-primary)',
              padding: '1rem',
              borderBottom: '1px solid var(--color-divider)'
            }}
          >
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1 min-width-0">
                <div className="d-flex align-items-center mb-1">
                  {getMechanismBadge(communication.type)}
                  {getCommunicationTypeBadge(communication.communicationType)}
                  {communication.dateSent ? (
                    <Badge color="green" variant="outline" size="sm">Sent</Badge>
                  ) : (
                    <Badge color="yellow" variant="outline" size="sm">Pending</Badge>
                  )}
                </div>
                
                <div className="mb-1">
                  <strong className="text-truncate d-block" style={{ color: 'var(--color-text-primary)' }}>
                    To: {getRecipients(communication.to)}
                  </strong>
                </div>
                
                <div className="mb-1">
                  <small className="text-truncate d-block" style={{ color: 'var(--color-text-medium)' }}>
                    {communication.email?.subject || 'No subject'}
                  </small>
                </div>
              </div>
              
              <div className="text-end ms-2">
                <small style={{ color: 'var(--color-text-medium)' }}>
                  {formatDate(communication.date)}
                </small>
              </div>
            </div>
          </div>
        ))}
      </Stack>
      
      {renderPagination()}
      
      {loading && communications.length > 0 && (
        <div className="text-center p-2">
          <small className="text-muted">Loading...</small>
        </div>
      )}
    </div>
  );
};

export default CommunicationsList;
